import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

/**
 * Shared "return to parent" nav element.
 *
 * Rendered near the top-left of deep pages (concierge detail views, service
 * detail, brand detail, private project detail, insight detail…). The
 * subtle champagne-gold styling matches the site's minimal editorial voice
 * — a hairline back-arrow, not a bold button.
 *
 * Behavior:
 *   - `to` prop present → renders `<Link to={to}>` (deterministic parent)
 *   - `to` absent       → renders `<button onClick={navigate(-1)}>`
 *     (history-based; useful when the user could have arrived from
 *     multiple places).
 *
 * Every instance carries `data-testid="back-link"` for the tester to
 * enumerate. Callers can pass `className` for one-off positioning tweaks.
 */
export default function BackLink({ to, label = "Back", className = "" }) {
    const navigate = useNavigate();
    const base =
        "inline-flex items-center gap-1.5 text-[13px] text-[#9A958A] " +
        "hover:text-[#C9A86A] hover:underline transition-colors duration-200 " +
        "focus:outline-none focus-visible:text-[#C9A86A] focus-visible:underline";
    const inner = (
        <>
            <ChevronLeft
                size={14}
                strokeWidth={1.6}
                aria-hidden="true"
                className="shrink-0"
            />
            <span>{label}</span>
        </>
    );
    if (to) {
        return (
            <Link
                to={to}
                className={`${base} ${className}`.trim()}
                data-testid="back-link"
            >
                {inner}
            </Link>
        );
    }
    return (
        <button
            type="button"
            onClick={() => navigate(-1)}
            className={`${base} ${className}`.trim()}
            data-testid="back-link"
        >
            {inner}
        </button>
    );
}
